import React from "react";
import { Button, Message } from "semantic-ui-react";
import SupportQuestionMark from "../../supportbeacon/SupportQuestionMark";
import { type Line, type SignType } from "../../eSignLanding/drawing/models";
import { svgToLines } from "../../eSignLanding/utils";
import SignatureSelection from "./SignatureSelection";
import { routes } from "utilities/routing/generatedRoutes";
import { hasDrawnProperSignature } from "eSignLanding/drawing/utils";
import EditViewPanel from "components/panels/EditViewPanel";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

const App = () => {
  const [isLoading, setIsLoading] = React.useState(true);

  const [signType, setSignType] = React.useState<SignType>();
  const [uploadError, setUploadError] = React.useState<string>(undefined);
  const [uploadedFileId, setUploadedFile] = React.useState<string>();
  const [signatureLines, setSignatureLines] = React.useState<Line[]>([]);
  const [hasTooSmallSignature, setHasTooSmallSignature] = React.useState<boolean>(false);

  const [isAutoSigningEnabled, setIsAutoSigningEnabled] = React.useState<boolean>(false);

  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const [serverErrors, setServerErrors] = React.useState<string[]>(undefined);

  const svgRef = React.useRef<SVGSVGElement>(undefined);

  const fetchSignatureSettings = async () => {
    const res = await window.CoreApi.UserSignature.GetUserSignature();

    if (res.data?.signature.signatureSvg != null) {
      setSignType("draw");
      setSignatureLines(svgToLines(res.data.signature.signatureSvg));
    } else if (res.data?.signature.signatureFileId != null) {
      setSignType("imageFile");
      setUploadedFile(res.data.signature.signatureFileId);
    } else {
      setSignType("draw");
    }

    setIsAutoSigningEnabled(res.data?.signAutomatically ?? false);
    setIsLoading(false);
  };

  React.useEffect(() => {
    fetchSignatureSettings();
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    if (signType === "draw" && !hasDrawnProperSignature(signatureLines)) {
      setHasTooSmallSignature(true);
      return;
    }

    setIsSaving(true);
    const res = await window.CoreApi.UserSignature.SetUserSignature({
      signAutomatically: isAutoSigningEnabled,
      signatureFileId: signType === "imageFile" ? uploadedFileId : undefined,
      signatureSvg: signType === "draw" ? svgRef.current.innerHTML : undefined
    });

    if (res.data.errors.length > 0) {
      setIsSaving(false);
      setServerErrors(res.data.errors.map(e => e.message));
    } else {
      window.location.href = routes.personal_SettingMenu().absolute;
    }
  };

  return <EditViewPanel
    isLoading={isLoading}
    title={RESX.ESign.eSignSignatureSettings}
  >
    <table className="s1_table clean rowspace">
      <tbody>
        <tr>
          <td>{RESX.ESign.Signature}</td>
          <td>
            <SignatureSelection
              signType={signType}
              setSignType={setSignType}
              signatureLines={signatureLines}
              setSignatureLines={setSignatureLines}
              uploadedFile={uploadedFileId}
              setUploadedFile={setUploadedFile}
              uploadError={uploadError}
              setUploadError={setUploadError}
              hasEmptySignature={hasTooSmallSignature}
              svgRef={svgRef}
            />
          </td>
        </tr>

        <tr>
          <td>{RESX.ESign.AutoSign}</td>
          <td className="flex">
            <div className="margin-right">
              <Checkbox
                checked={isAutoSigningEnabled}
                onChange={() => { setIsAutoSigningEnabled(prevVal => !prevVal); }}
              /></div>
            <div>
              <SupportQuestionMark articleId={serverReference.supportArticleIds.autoSign} />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    {serverErrors && <Message color="red" className="margin-top">
      {serverErrors.map((error) => <div key={error}>{error}</div>)}
    </Message>}

    <div className="flex margin-top-bigger">
      <div className="grow1"></div>
      <Button primary onClick={submit} loading={isSaving} disabled={isSaving}>
        {RESX.Buttons.btnSave}
      </Button>
    </div>
  </EditViewPanel>;
};

export default App;